/**
 * "Do Not Sell or Share My Personal Information" — added to Consentmo's cookie
 * preferences dialog.
 * ---------------------------------------------------------------------------
 * Consentmo's preferences dialog offers Necessary / Statistics / Marketing /
 * Preferences and nothing else. It never writes Shopify's fourth consent key:
 * measured on production 2026-08-26, every one of its save buttons calls
 * `Shopify.customerPrivacy.setTrackingConsent({analytics, marketing,
 * preferences})` — `sale_of_data` is absent from the payload. So the CCPA/CPRA
 * opt-out has no control anywhere in the banner, and nothing here can be
 * clobbered by the app: we own that key alone.
 *
 * This script adds the missing row and writes that key. It does not gate any
 * tracker — snippets/consent-mode.liquid already reads `sale_of_data` off the
 * Customer Privacy API and maps it onto Meta LDU, Criteo GPC and the rest, and
 * it re-runs on every `visitorConsentCollected`. Writing the key here is all
 * that is needed for the rest of the chain to react.
 *
 * The one thing it does reach outside its own row for is Marketing, which it
 * forces off and disables while the opt-out is on — those two controls are the
 * same question under CPRA and must not be able to disagree. See MARKETING IS
 * PART OF THE SAME ANSWER below.
 *
 * HOW THE ROW IS BUILT
 * Consentmo renders the dialog into an open shadow root on <csm-cookie-consent>,
 * styled with hashed CSS-module class names (`._j2FNJIiH0HeBo3O0zUQ`) that
 * change on every app release. Hand-written markup would need those hashes to
 * look native, and would break the next time the app ships. So the row is a
 * CLONE of the last real category section, with its text, ids and `for` target
 * rewritten. It inherits whatever the app's stylesheet says today, including
 * dark mode, without naming a single hashed class.
 *
 * The stable hooks it does rely on, all of them app-authored semantic classes
 * that have survived Consentmo's own theming API:
 *   .csm-dpp-section        one category block (the clone source)
 *   .cc-checkbox-container  title + switch row inside it
 *   .csm-dialog-switch      the switch <label> (wraps .csm-sr-only, input, slider)
 *   .cc-cookie-category-text  the description <p>
 *   .csm-shrinkable-content   the per-category cookie list (dropped from the clone)
 *   .cc-btn + .cc-allow / .cc-deny / .cc-settings   the three footer buttons
 * If Consentmo ever renames these the row simply stops appearing — it fails
 * closed and never renders a half-built control.
 *
 * WHEN IT RENDERS
 * Only where `Shopify.customerPrivacy.saleOfDataRegion()` is true. Outside those
 * US states the key is not settable and the control would be a lie.
 *
 * WRITE ORDER
 * The write is deferred until after Consentmo's own save lands
 * (`visitorConsentCollected`, with a timeout fallback) so that the analytics /
 * marketing / preferences values we send alongside are the ones the visitor
 * just chose, not the stale pre-click ones.
 *
 * Config comes from window.AltenewSaleOfData, set by
 * snippets/consentmo-sale-of-data.liquid.
 */
(function (w, d) {
  'use strict';

  var CFG = w.AltenewSaleOfData || {};
  var TITLE = CFG.title || 'Do Not Sell or Share My Personal Information';
  var BODY = CFG.description || '';
  var GPC_NOTICE = CFG.gpcNotice || '';
  var MARKETING_NOTICE = CFG.marketingLockNotice || '';

  /*
    Global Privacy Control. A browser sending this header has already declared
    an opt-out, and Shopify treats it as binding: `sale_of_data` is pinned to
    "no" and setTrackingConsent silently refuses to set it back to "yes".
    Verified on production 2026-08-27 — in the same call, `analytics` flipped
    and `sale_of_data: true` did not, with no error on the callback.

    Left alone, the control would look broken to exactly the people who care
    most about it: switch it off, save, reopen, and it is on again with no
    explanation. So under GPC the row renders opted-out and disabled, says why,
    and writes nothing.
  */
  var GPC = !!(w.navigator && w.navigator.globalPrivacyControl === true);

  // What "Accept All" does to this key. See the snippet for the trade-off.
  //   'preserve' — leave an existing opt-out alone unless the visitor actually
  //                moved this toggle during the session (default).
  //   'follow'   — Accept All means allow the sale too, like every other row.
  var ACCEPT_ALL = CFG.acceptAllBehavior === 'follow' ? 'follow' : 'preserve';

  var SECTION_ID = 'altenew-dnss-section';
  var INPUT_ID = 'altenew-dnss-input';
  var INFO_ID = 'altenew-dnss-info';

  /*
    MARKETING IS PART OF THE SAME ANSWER
    Under CPRA "share" means disclosing personal information for cross-context
    behavioural advertising — which is exactly what the Marketing category is.
    Leaving both switchable independently lets a visitor say "do not share" and
    "yes to targeted ads" in the same dialog, and whichever they touched last
    quietly wins. So while this opt-out is on, Marketing is forced off, disabled
    and captioned; turning the opt-out back off releases it but does NOT switch
    it back on — that is the visitor's call to make again.

    Read off the live dialog 2026-09-04. Both hooks are app-authored semantic
    names of the same kind the rest of this file leans on, not hashed CSS
    modules. If Consentmo renames them the lock quietly stops applying, which is
    why write() enforces the same rule again on the way out.
  */
  var MARKETING_INPUT_ID = 'csm-dialog-preferences-marketing-input';
  var MARKETING_TITLE_CLASS = 'isense-cc-prefence-title-marketing';

  // The app's own Marketing copy, stashed on first lock so the notice can be
  // appended and removed without accumulating.
  var marketingBody = null;

  // Set once the visitor moves our switch, so "Accept All" in 'preserve' mode can
  // tell "they left it alone" from "they chose this". Cleared after every write.
  var touched = false;

  // Pending write, held until Consentmo's own save has landed.
  var wanted = null; // 'yes' | 'no' | null
  var wantedTimer = null;

  function shadow() {
    var host = d.querySelector('csm-cookie-consent');
    return host && host.shadowRoot;
  }

  function api() {
    return w.Shopify && w.Shopify.customerPrivacy;
  }

  function inRegion() {
    var cp = api();
    try {
      return !!(cp && typeof cp.saleOfDataRegion === 'function' && cp.saleOfDataRegion());
    } catch (e) {
      return false;
    }
  }

  function consent() {
    var cp = api();
    try {
      return (cp && cp.currentVisitorConsent()) || {};
    } catch (e) {
      return {};
    }
  }

  // Toggle ON means "do not sell", i.e. sale_of_data === 'no'. Inverted against
  // its three neighbours on purpose — that is the wording the law uses and what
  // the description text spells out.
  function isOptedOut() {
    return consent().sale_of_data === 'no';
  }

  /* ------------------------------------------------------- the marketing row */

  // Id first, title class as the fallback — two independent hooks so a rename of
  // either one alone does not lose the row.
  function marketingRow(sr) {
    if (!sr) return null;
    var input = sr.querySelector('#' + MARKETING_INPUT_ID);
    var section = input && input.closest('.csm-dpp-section');
    if (!input) {
      var title = sr.querySelector('.' + MARKETING_TITLE_CLASS);
      section = title && title.closest('.csm-dpp-section');
      input = section && section.querySelector('input');
    }
    if (!input) return null;
    return {
      input: input,
      toggle: input.closest('.csm-dialog-switch'),
      text: section && section.querySelector('.cc-cookie-category-text')
    };
  }

  function lockMarketing(sr, locked) {
    var row = marketingRow(sr);
    if (!row) return;

    // Setting .checked in script fires no change event, so Consentmo's own
    // handler would never learn the row moved and its save would still send the
    // old value. Announce it.
    if (locked && row.input.checked) {
      setSwitch(row.toggle, row.input, false);
      try {
        row.input.dispatchEvent(new Event('change', { bubbles: true }));
      } catch (e) {}
    }

    row.input.disabled = !!locked;
    if (locked) row.input.setAttribute('aria-disabled', 'true');
    else row.input.removeAttribute('aria-disabled');

    /*
      Consentmo paints its disabled look from a class its own script owns, not
      from [disabled] — the Necessary row proves it, and a disabled input styled
      by nothing renders identically to a live one. Verified on production
      2026-09-04. Without a cue the row is functionally locked and visually
      normal, so it reads as a broken switch rather than a consequence.

      Inline, because the dialog lives in a shadow root a theme stylesheet
      cannot reach, and because two properties are not worth injecting a
      <style> node into somebody else's tree.
    */
    if (row.toggle) {
      row.toggle.style.opacity = locked ? '0.5' : '';
      row.toggle.style.cursor = locked ? 'not-allowed' : '';
    }

    if (row.text && MARKETING_NOTICE) {
      if (marketingBody === null) marketingBody = row.text.textContent;
      row.text.textContent = locked ? marketingBody + ' ' + MARKETING_NOTICE : marketingBody;
    }
  }

  /* ---------------------------------------------------------------- writing */

  function write(allowSale) {
    var cp = api();
    if (!cp || typeof cp.setTrackingConsent !== 'function' || !inRegion()) return;

    var now = consent();

    /*
      An opt-out drags marketing down with it — see MARKETING IS PART OF THE SAME
      ANSWER above. The UI lock is the visible half; this is the half that
      actually binds, because it also catches the paths the dialog never sees:
      Accept All turning marketing on a beat before our write lands, a stored
      consent from before this rule existed, or Consentmo renaming the hooks the
      lock relies on. Turning the sale back ON never turns marketing back on —
      that stays whatever the visitor last chose.
    */
    var marketing = allowSale ? now.marketing === 'yes' : false;

    var saleSettled = now.sale_of_data === (allowSale ? 'yes' : 'no');
    var marketingSettled = marketing === (now.marketing === 'yes');
    if (saleSettled && marketingSettled) return; // already there

    try {
      cp.setTrackingConsent(
        {
          analytics: now.analytics === 'yes',
          marketing: marketing,
          preferences: now.preferences === 'yes',
          sale_of_data: !!allowSale
        },
        function () {}
      );
    } catch (e) {}
  }

  function flush() {
    if (wanted === null) return;
    var want = wanted;
    wanted = null;
    touched = false;
    if (wantedTimer) {
      clearTimeout(wantedTimer);
      wantedTimer = null;
    }
    write(want === 'yes');
  }

  // Queue the write for just after Consentmo's. `visitorConsentCollected` is the
  // signal that its setTrackingConsent resolved; the timer covers the case where
  // its save is a no-op (nothing changed) and no event ever fires.
  function queue(want) {
    wanted = want;
    if (wantedTimer) clearTimeout(wantedTimer);
    wantedTimer = setTimeout(flush, 1200);
  }

  d.addEventListener('visitorConsentCollected', function () {
    if (wanted !== null) setTimeout(flush, 0);
  });

  /* --------------------------------------------------------------- the row */

  // The app paints the slider from an `active` class on the switch label, not
  // from `:checked` — its own script owns that class and knows nothing about
  // this row. Drive it ourselves or the switch is decorative: it sits in
  // whatever position the cloned category was in and never moves.
  function setSwitch(toggle, input, on) {
    input.checked = !!on;
    if (toggle) toggle.classList.toggle('active', !!on);
  }

  function build(template) {
    var node = template.cloneNode(true);

    // Drop the cloned category's cookie list — this row has no cookies of its own.
    Array.prototype.slice
      .call(node.querySelectorAll('.csm-shrinkable-content'))
      .forEach(function (el) {
        el.parentNode.removeChild(el);
      });

    /*
      A clone carries every id AND every id-reference belonging to the category
      it was copied from. Both directions bite:

        - the switch label's `for` still names that category's checkbox, and a
          `<label for>` wins over the control it wraps — so clicking this
          toggle flipped Preferences instead;
        - the input's `aria-labelledby` still names that category's paragraph,
          and aria-labelledby outranks the label element, so a screen reader
          announced this control with the Preferences description.

      Strip every reference in the subtree, then wire up only the ones we mean.
      Cheaper to be exhaustive here than to rediscover the next stale attribute
      the day Consentmo adds one.
    */
    var REFS = ['id', 'for', 'aria-labelledby', 'aria-describedby', 'aria-controls', 'aria-owns'];
    [node].concat(Array.prototype.slice.call(node.querySelectorAll('*'))).forEach(function (el) {
      REFS.forEach(function (attr) {
        el.removeAttribute(attr);
      });
    });

    node.id = SECTION_ID;

    var box = node.querySelector('.cc-checkbox-container') || node;
    var toggle = node.querySelector('.csm-dialog-switch');
    var input = toggle && toggle.querySelector('input');
    var text = node.querySelector('.cc-cookie-category-text');
    if (!toggle || !input || !text) return null;

    // The title is the label in the row that is not the switch itself.
    var title = null;
    Array.prototype.slice.call(box.children).forEach(function (el) {
      if (!title && el.tagName === 'LABEL' && el !== toggle) title = el;
    });
    if (!title) return null;

    // Only the modifier class is ours to change. No `for` here on purpose: the
    // app's own title labels carry none, and adding one would name the input
    // twice and have a screen reader read the title out twice with it.
    title.className = title.className.replace(
      /isense-cc-prefence-title-\S+/,
      'isense-cc-prefence-title-saleofdata'
    );
    (title.querySelector('span') || title).textContent = TITLE;

    toggle.setAttribute('for', INPUT_ID);
    var srOnly = toggle.querySelector('.csm-sr-only');
    if (srOnly) srOnly.textContent = TITLE;

    input.id = INPUT_ID;
    input.setAttribute('aria-describedby', INFO_ID);
    if (GPC) {
      input.disabled = true;
      input.setAttribute('aria-disabled', 'true');
    } else {
      input.disabled = false;
      input.removeAttribute('disabled');
    }
    setSwitch(toggle, input, GPC || isOptedOut());
    input.addEventListener('change', function () {
      touched = true;
      setSwitch(toggle, input, input.checked);
      lockMarketing(shadow(), input.checked);
    });

    text.id = INFO_ID;
    text.textContent = GPC && GPC_NOTICE ? BODY + ' ' + GPC_NOTICE : BODY;

    return node;
  }

  function inject() {
    var sr = shadow();
    if (!sr || !inRegion()) return;

    var existing = sr.querySelector('#' + SECTION_ID);
    if (existing) {
      // Dialog reopened without a rebuild — just resync the switch.
      var live = existing.querySelector('input');
      if (live) setSwitch(existing.querySelector('.csm-dialog-switch'), live, GPC || isOptedOut());
      lockMarketing(sr, GPC || isOptedOut());
      return;
    }

    var sections = sr.querySelectorAll('.csm-dpp-section');
    if (!sections.length) return;

    var template = sections[sections.length - 1];
    var wrap = template.parentNode;
    var node = build(template);
    if (!node) return;

    // Sections run "…section, hr, section, hr, footer", so land after the
    // trailing rule and add one of our own to keep the rhythm.
    var after = template.nextElementSibling;
    var anchor = after && after.tagName === 'HR' ? after : template;
    wrap.insertBefore(node, anchor.nextSibling);

    var rule = wrap.querySelector('hr');
    if (rule) wrap.insertBefore(rule.cloneNode(true), node.nextSibling);

    // The Marketing row is Consentmo's, painted before ours existed, so it opens
    // switchable even when the visitor is already opted out. Bring it into line
    // on the first paint rather than waiting for someone to touch our toggle.
    lockMarketing(sr, GPC || isOptedOut());

    bind(sr);
  }

  /* ------------------------------------------------------------ the buttons */

  function bind(sr) {
    if (sr.__altenewDnssBound) return;
    sr.__altenewDnssBound = true;

    // Capture phase: Consentmo closes the dialog on click, so by the bubble phase
    // the button may already be detached. composedPath() crosses the shadow
    // boundary, which a plain event.target walk does not.
    sr.addEventListener(
      'click',
      function (ev) {
        var path = ev.composedPath ? ev.composedPath() : [ev.target];
        var btn = null;
        for (var i = 0; i < path.length; i++) {
          var el = path[i];
          if (el && el.classList && el.classList.contains('cc-btn')) {
            btn = el;
            break;
          }
        }
        if (!btn) return;

        // Under GPC the key is pinned to "no" and every write is refused, so
        // there is nothing to say. Leave the visitor's declared opt-out alone.
        if (GPC) return;

        var input = sr.querySelector('#' + INPUT_ID);
        var fromToggle = input && input.checked ? 'no' : 'yes';

        if (btn.classList.contains('cc-deny')) {
          // Reject All has to include the sale — it is the broadest refusal.
          queue('no');
        } else if (btn.classList.contains('cc-settings')) {
          queue(fromToggle);
        } else if (btn.classList.contains('cc-allow')) {
          if (ACCEPT_ALL === 'follow') queue('yes');
          else if (touched) queue(fromToggle); // honour a deliberate move
          // 'preserve' with an untouched toggle used to write nothing at all,
          // which was fine while this row stood alone. It no longer is: Accept
          // All switches marketing on, so a standing opt-out has to be
          // re-asserted to drag it back off. Same value, so no visible change
          // beyond the one the opt-out already implied.
          else if (isOptedOut()) queue('no');
        }
      },
      true
    );
  }

  d.addEventListener('consentmo:dialogOpened', function () {
    // The dialog paints over a frame or two; retry briefly rather than race it.
    var tries = 0;
    (function attempt() {
      inject();
      var sr = shadow();
      if ((!sr || !sr.querySelector('#' + SECTION_ID)) && ++tries < 20) {
        setTimeout(attempt, 100);
      }
    })();
  });
})(window, document);
